import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Button,
    Table,
    Modal,
    List,
    Alert,
    Row,
    Col,
    Space,
    Typography,
    Popconfirm,
    message
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    PlusOutlined
} from '@ant-design/icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import minMax from 'dayjs/plugin/minMax';
import { useTheme } from '../../../context/ThemeContext';
import { API_BASE_URL } from '../../../utils/config';
import { formatMoney, inputNumberMoneyProps } from '../../../utils/currency';
import axios from 'axios';
import CumulativeProfitChart from './CumulativeProfitChart';
import { 
    generateProjection, 
    calculateDateRange,
    convertDbRateToFrontendFormat 
} from '../../../utils/contractUtils';
import { useContracts } from '../../../hooks/useContracts';

dayjs.extend(minMax);
dayjs.locale('tr');

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const { Option } = Select;
const { Text } = Typography;

function OngoruPage() {
    const { maptriksId } = useParams();
    const { darkMode } = useTheme();
    const [bankContracts, setBankContracts] = useState([]);
    const [rentContracts, setRentContracts] = useState([]);
    const [inflationRates, setInflationRates] = useState({});
    const [projection, setProjection] = useState([]);
    const [trendChanges, setTrendChanges] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const isRecalculatingRef = useRef(false);
    const [editBankModalVisible, setEditBankModalVisible] = useState(false);
    const [editRentModalVisible, setEditRentModalVisible] = useState(false);
    const [editingBankIndex, setEditingBankIndex] = useState(null);
    const [editingRentIndex, setEditingRentIndex] = useState(null);
    const [bankForm] = Form.useForm();
    const [rentForm] = Form.useForm();
    const [inflationForm] = Form.useForm();
    const [editBankForm] = Form.useForm();
    const [editRentForm] = Form.useForm();

    const {
        bankContracts: hookBankContracts,
        rentContracts: hookRentContracts,
        fetchBankContracts: hookFetchBankContracts,
        fetchRentContracts: hookFetchRentContracts,
        setBankContracts: hookSetBankContracts,
        setRentContracts: hookSetRentContracts
    } = useContracts();

    useEffect(() => {
        setBankContracts(hookBankContracts);
    }, [hookBankContracts, setBankContracts]);

    useEffect(() => {
        setRentContracts(hookRentContracts);
    }, [hookRentContracts, setRentContracts]);

    const fetchBankContracts = async () => {
        const contracts = await hookFetchBankContracts(maptriksId);
        return contracts;
    };

    const fetchRentContracts = async () => {
        const contracts = await hookFetchRentContracts(maptriksId);
        return contracts;
    };

    useEffect(() => {
        const loadData = async () => {
            if (maptriksId) {
                setIsInitialLoad(true);
                try {
                    const [bankData, rentData] = await Promise.all([
                        fetchBankContracts(),
                        fetchRentContracts()
                    ]);
                    const inflation = JSON.parse(localStorage.getItem('inflationRates') || '{}');
                    setInflationRates(inflation);
                    setBankContracts(bankData || []);
                    setRentContracts(rentData || []);
                    recalculateAll(bankData || [], rentData || [], inflation);
                    setIsInitialLoad(false);
                } catch (error) {
                    console.error('Veri yükleme hatası:', error);
                    const inflation = JSON.parse(localStorage.getItem('inflationRates') || '{}');
                    setBankContracts([]);
                    setRentContracts([]);
                    recalculateAll([], [], inflation);
                    setIsInitialLoad(false);
                }
            }
        };
        loadData();
    }, [maptriksId]);

    useEffect(() => {
        if (!isInitialLoad && (bankContracts.length > 0 || rentContracts.length > 0)) {
            recalculateAll(null, null, inflationRates);
        }
    }, [bankContracts, rentContracts, inflationRates, isInitialLoad]);

    const formatCurrency = (amount) => formatMoney(amount);

    const formatDate = (date) => {
        const d = dayjs(date);
        return d.format('MMMM YYYY');
    };

    const findTrendChanges = (data) => {
        if (!data || data.length < 2) return [];

        const trendChanges = [];
        let lastTrend = null;
        let lastProfit = 0;

        trendChanges.push({
            date: data[0].date,
            trend: data[0].profit >= 0 ? 'up' : 'down',
            profit: data[0].profit,
            cumulativeProfit: data[0].cumulativeProfit,
            reason: 'Başlangıç',
            details: {
                bankDetails: data[0].bankDetails,
                rentDetails: data[0].rentDetails
            }
        });

        for (let i = 1; i < data.length; i++) {
            const currentProfit = data[i].profit;
            const currentDate = data[i].date;
            let currentTrend;
            if (currentProfit > lastProfit) {
                currentTrend = 'up';
            } else if (currentProfit < lastProfit) {
                currentTrend = 'down';
            } else {
                currentTrend = lastTrend;
            }
            if (lastTrend !== null && currentTrend !== lastTrend) {
                const reason = findChangeReason(data[i - 1], data[i]);
                trendChanges.push({
                    date: currentDate,
                    trend: currentTrend,
                    profit: currentProfit,
                    cumulativeProfit: data[i].cumulativeProfit,
                    reason: reason,
                    details: {
                        bankDetails: data[i].bankDetails,
                        rentDetails: data[i].rentDetails,
                        previousBankDetails: data[i - 1].bankDetails,
                        previousRentDetails: data[i - 1].rentDetails
                    }
                });
            }
            lastTrend = currentTrend;
            lastProfit = currentProfit;
        }

        return trendChanges;
    };

    const findChangeReason = (prevData, currentData) => {
        const reasons = [];

        const prevBankNames = prevData.bankDetails.map(d => d.name);
        const currentBankNames = currentData.bankDetails.map(d => d.name);
        const newBanks = currentBankNames.filter(name => !prevBankNames.includes(name));
        if (newBanks.length > 0) {
            reasons.push(`Yeni banka geliri: ${newBanks.join(', ')}`);
        }
        const removedBanks = prevBankNames.filter(name => !currentBankNames.includes(name));
        if (removedBanks.length > 0) {
            reasons.push(`Banka geliri sona erdi: ${removedBanks.join(', ')}`);
        }

        const prevRentNames = prevData.rentDetails.map(d => d.name);
        const currentRentNames = currentData.rentDetails.map(d => d.name);
        const newRents = currentRentNames.filter(name => !prevRentNames.includes(name));
        if (newRents.length > 0) {
            reasons.push(`Yeni kira gideri: ${newRents.join(', ')}`);
        }
        const removedRents = prevRentNames.filter(name => !currentRentNames.includes(name));
        if (removedRents.length > 0) {
            reasons.push(`Kira gideri sona erdi: ${removedRents.join(', ')}`);
        }

        const commonRents = prevRentNames.filter(name => currentRentNames.includes(name));
        for (const rentName of commonRents) {
            const prevRent = prevData.rentDetails.find(d => d.name === rentName);
            const currentRent = currentData.rentDetails.find(d => d.name === rentName);
            if (prevRent && currentRent && prevRent.amount !== currentRent.amount) {
                const difference = currentRent.amount - prevRent.amount;
                const percentChange = ((difference / prevRent.amount) * 100).toFixed(2);
                if (difference > 0) {
                    reasons.push(`${rentName} kirası %${percentChange} arttı`);
                } else {
                    reasons.push(`${rentName} kirası %${Math.abs(percentChange)} azaldı`);
                }
            }
        }

        const prevMultiplier = prevData.rentDetails.find(d => d.bankMultiplier)?.bankMultiplier || 1;
        const currentMultiplier = currentData.rentDetails.find(d => d.bankMultiplier)?.bankMultiplier || 1;
        if (prevMultiplier !== currentMultiplier) {
            reasons.push(`Banka çarpanı ${prevMultiplier} → ${currentMultiplier} oldu`);
        }

        if (reasons.length === 0) {
            if (currentData.profit > prevData.profit) {
                reasons.push('Kar arttı');
            } else {
                reasons.push('Kar azaldı');
            }
        }
        return reasons.join(', ');
    };

    const recalculateAll = (overrideBankContracts = null, overrideRentContracts = null, overrideInflationRates = null) => {
        if (isRecalculatingRef.current) {
            return;
        }
        isRecalculatingRef.current = true;   
        const banksToUse = overrideBankContracts !== null ? overrideBankContracts : bankContracts;
        const rentsToUse = overrideRentContracts !== null ? overrideRentContracts : rentContracts;
        const inflationToUse = overrideInflationRates !== null ? overrideInflationRates : inflationRates;
            
        const { startDate, endDate } = calculateDateRange(banksToUse, rentsToUse);
        if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
            console.error('Geçersiz tarih aralığı hesaplandı!', {
                startDate: startDate?.format?.('YYYY-MM-DD') || startDate,
                endDate: endDate?.format?.('YYYY-MM-DD') || endDate,
                bankCount: banksToUse.length,
                rentCount: rentsToUse.length
            });
            setProjection([]);
            setTrendChanges([]);
            return;
        }
        
        if (startDate.isAfter(endDate)) {
            console.error('Başlangıç tarihi bitiş tarihinden sonra!', {
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD')
            });
            setProjection([]);
            setTrendChanges([]);
            return;
        }
        
        const filteredBankContracts = banksToUse.filter(contract => {
            const contractStart = dayjs(contract.startDate);
            const contractEnd = contract.endDate ? dayjs(contract.endDate) : dayjs('2100-12-31');
            return (contractStart.isBefore(endDate) || contractStart.isSame(endDate)) && 
                   (contractEnd.isAfter(startDate) || contractEnd.isSame(startDate));
        });
        
        const filteredRentContracts = rentsToUse.filter(contract => {
            const contractStart = dayjs(contract.startDate);
            const contractEnd = contract.endDate ? dayjs(contract.endDate) : dayjs('2100-12-31');
            return (contractStart.isBefore(endDate) || contractStart.isSame(endDate)) && 
                   (contractEnd.isAfter(startDate) || contractEnd.isSame(startDate));
        });

        const proj = generateProjection(filteredBankContracts, filteredRentContracts, startDate, endDate, inflationToUse);
        setProjection(proj);
        setTrendChanges(findTrendChanges(proj));
        
        if (proj.length > 0 && maptriksId) {
            saveOrUpdateChartToDB(proj).catch(err => {
                console.error('Grafik kaydetme hatası (recalculateAll):', err.response?.data || err.message);
            });
        }
        isRecalculatingRef.current = false;
    };

    const saveOrUpdateChartToDB = async (proj) => {
        if (!maptriksId || !proj || proj.length === 0) {
            return;
        }

        const formatDate = (date) => {
            const d = dayjs(date);
            return d.format('MMMM YYYY');
        };

        const newChartData = {
            labels: proj.map(item => formatDate(item.date)),
            data: proj.map(item => item.cumulativeProfit || 0),
            projection: proj.map(item => ({
                ...item,
                date: item.date instanceof Date ? item.date.toISOString() : (item.date ? new Date(item.date).toISOString() : new Date().toISOString())
            }))
        };

        try {
            const existingChartResponse = await axios.get(
                `${API_BASE_URL}/ongoru/grafik/${maptriksId}?chart_type=birikimli_kar_zarar`,
                {
                    headers: {
                        ...(localStorage.getItem('auth_token') && {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                    }
                }
            );

            if (existingChartResponse.data && existingChartResponse.data.success && existingChartResponse.data.data) {
                const existingData = existingChartResponse.data.data.slice_config;
                const newHash = JSON.stringify(newChartData.data);
                const existingHash = JSON.stringify(existingData?.data || []);
                if (newHash !== existingHash) {
                    const updateResponse = await axios.post(
                        `${API_BASE_URL}/ongoru/grafik/kaydet`,
                        {
                            maptriksID: parseInt(maptriksId),
                            chart_type: 'birikimli_kar_zarar',
                            slice_config: newChartData
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                ...(localStorage.getItem('auth_token') && {
                                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                                })
                            }
                        }
                    );
                }
            } else {
                const createResponse = await axios.post(
                    `${API_BASE_URL}/ongoru/grafik/kaydet`,
                    {
                        maptriksID: parseInt(maptriksId),
                        chart_type: 'birikimli_kar_zarar',
                        slice_config: newChartData
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            ...(localStorage.getItem('auth_token') && {
                                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                            })
                        }
                    }
                );
            }
        } catch (error) {
            if (error.response?.status === 404) {
                try {
                    const createResponse = await axios.post(
                        `${API_BASE_URL}/ongoru/grafik/kaydet`,
                        {
                            maptriksID: parseInt(maptriksId),
                            chart_type: 'birikimli_kar_zarar',
                            slice_config: newChartData
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                ...(localStorage.getItem('auth_token') && {
                                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                                })
                            }
                        }
                    );
                } catch (saveError) {
                    console.error('Grafik kaydetme hatası (404 sonrası):', saveError.response?.data || saveError.message);
                }
            } else {
                console.error('Grafik kaydetme/güncelleme hatası:', {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    message: error.message
                });
            }
        }
    };

    const convertRateToDbFormat = (rateValue) => {
        if (rateValue === '0' || rateValue === 0) {
            return 0; 
        } else if (rateValue === 'ufe') {
            return 1; 
        } else if (rateValue === 'tufe') {
            return 2; 
        } else if (rateValue === 'ufetufe') {
            return 3; 
        } else if (['0.25', '0.30', '0.35'].includes(rateValue)) {
            return 5; 
        }
        return 0;
    };

    const handleBankSubmit = async (values) => {
        const payload = {
            ad: values.name,
            kiraBedeli: values.amount,
            kiraOdemeBaslangic: values.startDate.format('YYYY-MM-DD'),
            sozlesmeBitis: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            maptriksID: parseInt(maptriksId),
            artisOrani: convertRateToDbFormat(values.rate)
        };

        const response = await axios.post(
            `${API_BASE_URL}/list/banka-sozlesme-ekle`,
            payload,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...(localStorage.getItem('auth_token') && {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    })
                }
            }
        );

        if (response.data.success) {
            message.success('Banka sözleşmesi başarıyla eklendi');
            bankForm.resetFields();
            const updatedContracts = await fetchBankContracts();
            if (updatedContracts) {
                recalculateAll(updatedContracts, null, inflationRates);
            } else {
                recalculateAll(null, null, inflationRates);
            }
        } else {
            message.error(response.data.message || 'Banka sözleşmesi eklenirken hata oluştu');
        }
    };

    const handleRentSubmit = async (values) => {
        try {
            const payload = {
                ad: values.name,
                kiraBedeli: values.amount,
                sozlesme_kiraBaslangic: values.startDate.format('YYYY-MM-DD'),
                sozlesmeBitis: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
                banka_girdikce_kira: values.paymentType,
                maptriksID: parseInt(maptriksId),
                artisOrani: convertRateToDbFormat(values.rate)
            };

            const response = await axios.post(
                `${API_BASE_URL}/list/yer-sozlesme-ekle`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('auth_token') && {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                    }
                }
            );

            if (response.data.success) {
                message.success('Yer sözleşmesi başarıyla eklendi');
                rentForm.resetFields();
                const updatedContracts = await fetchRentContracts();
                if (updatedContracts) {
                    recalculateAll(null, updatedContracts);
                } else {
                    recalculateAll();
                }
            } else {
                message.error(response.data.message || 'Yer sözleşmesi eklenirken hata oluştu');
            }
        } catch (error) {
            console.error('Yer sözleşmesi ekleme hatası:', error);
            message.error(error.response?.data?.detail || error.response?.data?.message || 'Yer sözleşmesi eklenirken hata oluştu');
        }
    };

    const handleInflationSubmit = (values) => {
        const newRates = {
            ...inflationRates,
            [values.year]: {
                ufe: values.ufe,
                tufe: values.tufe
            }
        };
        setInflationRates(newRates);
        localStorage.setItem('inflationRates', JSON.stringify(newRates));
        inflationForm.resetFields();
        recalculateAll();
    };

    const deleteBankContract = async (index) => {
        const contract = bankContracts[index];
        try {
            const response = await axios.put(
                `${API_BASE_URL}/list/generic/update-active`,
                {
                    table_name: 'sozlesmeler',
                    id: contract.id,
                    active: 0
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('auth_token') && {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                    }
                }
            );

            if (response.data.success) {
                message.success('Banka sözleşmesi başarıyla silindi');
                const updatedContracts = await fetchBankContracts();
                if (updatedContracts) {
                    recalculateAll(updatedContracts, null);
                } else {
                    recalculateAll();
                }
            } else {
                message.error(response.data.message || 'Banka sözleşmesi silinirken hata oluştu');
            }
        } catch (error) {
            console.error('Banka sözleşmesi silme hatası:', error);
            message.error(error.response?.data?.detail || 'Banka sözleşmesi silinirken hata oluştu');
        }
    };

    const deleteRentContract = async (index) => {
        const contract = rentContracts[index];
        try {
            const response = await axios.put(
                `${API_BASE_URL}/list/generic/update-active`,
                {
                    table_name: 'sozlesmeler',
                    id: contract.id,
                    active: 0
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('auth_token') && {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                    }
                }
            );

            if (response.data.success) {
                message.success('Yer sözleşmesi başarıyla silindi');
                const updatedContracts = await fetchRentContracts();
                if (updatedContracts) {
                    recalculateAll(null, updatedContracts);
                } else {
                    recalculateAll();
                }
            } else {
                message.error(response.data.message || 'Yer sözleşmesi silinirken hata oluştu');
            }
        } catch (error) {
            console.error('Yer sözleşmesi silme hatası:', error);
            message.error(error.response?.data?.detail || 'Yer sözleşmesi silinirken hata oluştu');
        }
    };

    const deleteInflationRate = (year) => {
        const newRates = { ...inflationRates };
        delete newRates[year];
        setInflationRates(newRates);
        localStorage.setItem('inflationRates', JSON.stringify(newRates));
        recalculateAll();
    };

    const toggleBankContractActive = (index) => {
        const newBanks = [...bankContracts];
        if (newBanks[index].active === undefined) {
            newBanks[index].active = false;
        } else {
            newBanks[index].active = !newBanks[index].active;
        }
        setBankContracts(newBanks);
        localStorage.setItem('bankContracts', JSON.stringify(newBanks));
        recalculateAll(newBanks, null);
    };

    const editBankContract = (index) => {
        const contract = bankContracts[index];
        setEditingBankIndex(index);
        const frontendRate = typeof contract.rate === 'number' || (typeof contract.rate === 'string' && /^\d+$/.test(contract.rate))
            ? convertDbRateToFrontendFormat(parseInt(contract.rate))
            : contract.rate;
        editBankForm.setFieldsValue({
            name: contract.name,
            amount: contract.amount,
            startDate: dayjs(contract.startDate),
            endDate: contract.endDate ? dayjs(contract.endDate) : null,
            rate: frontendRate
        });
        setEditBankModalVisible(true);
    };

    const editRentContract = (index) => {
        const contract = rentContracts[index];
        setEditingRentIndex(index);
        const frontendRate = typeof contract.rate === 'number' || (typeof contract.rate === 'string' && /^\d+$/.test(contract.rate))
            ? convertDbRateToFrontendFormat(parseInt(contract.rate))
            : contract.rate;
        editRentForm.setFieldsValue({
            name: contract.name,
            amount: contract.amount,
            startDate: dayjs(contract.startDate),
            endDate: contract.endDate ? dayjs(contract.endDate) : null,
            paymentType: contract.paymentType,
            rate: frontendRate
        });
        setEditRentModalVisible(true);
    };

    const handleBankEditSubmit = (values) => {
        const newBanks = [...bankContracts];
        newBanks[editingBankIndex] = {
            ...newBanks[editingBankIndex],
            name: values.name,
            amount: values.amount,
            startDate: values.startDate.format('YYYY-MM-DD'),
            endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            rate: convertRateToDbFormat(values.rate)
        };
        setBankContracts(newBanks);
        localStorage.setItem('bankContracts', JSON.stringify(newBanks));
        setEditBankModalVisible(false);
        setEditingBankIndex(null);
        recalculateAll(newBanks, null, inflationRates);
    };

    const handleRentEditSubmit = (values) => {
        const newRents = [...rentContracts];
        newRents[editingRentIndex] = {
            ...newRents[editingRentIndex],
            name: values.name,
            amount: values.amount,
            startDate: values.startDate.format('YYYY-MM-DD'),
            endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            paymentType: values.paymentType,
            rate: convertRateToDbFormat(values.rate)
        };
        setRentContracts(newRents);
        localStorage.setItem('rentContracts', JSON.stringify(newRents));
        setEditRentModalVisible(false);
        setEditingRentIndex(null);
        recalculateAll(null, newRents, inflationRates);
    };

    const convertDbRateToDisplay = (dbRate) => {
        const rateLabels = {
            0: 'Artış Yok',
            1: 'ÜFE',
            2: 'TÜFE',
            3: '(ÜFE+TÜFE)/2',
            5: 'Sabit Oran',
            6: '(ÜFE+TÜFE)/2 + Sabit Oran'
        };
        return rateLabels[dbRate] || `Bilinmeyen (${dbRate})`;
    };

    const getRateDisplay = (rate) => {
        if (typeof rate === 'number' || (typeof rate === 'string' && /^\d+$/.test(rate))) {
            return convertDbRateToDisplay(parseInt(rate));
        }
        if (['ufe', 'tufe', 'ufetufe'].includes(rate)) {
            const rateLabels = {
                'ufe': 'ÜFE',
                'tufe': 'TÜFE',
                'ufetufe': 'ÜFE+TÜFE/2'
            };
            return rateLabels[rate];
        }
        if (typeof rate === 'string' && ['0.25', '0.30', '0.35'].includes(rate)) {
            return `%${parseFloat(rate) * 100}`;
        }
        if (typeof rate === 'number' || !isNaN(parseFloat(rate))) {
            return `%${parseFloat(rate) * 100}`;
        }
        return rate || 'Belirtilmemiş';
    };

    const filteredProjection = projection && projection.length > 0 
        ? projection.filter(item => {
            if (!item || !item.date) return false;
            const itemDate = dayjs(item.date);
            return itemDate.isAfter(dayjs('2023-12-31')) || itemDate.isSame(dayjs('2023-12-31'));
        })
        : [];
    
    if (projection.length === 0) {
        console.warn('Projection state boş!', {
            projectionLength: projection.length,
            bankContractsLength: bankContracts.length,
            rentContractsLength: rentContracts.length,
            inflationRatesKeys: Object.keys(inflationRates).length
        });
    }

    const filteredTrendChanges = trendChanges.filter(item => {
        const itemDate = dayjs(item.date);
        if (item.reason === 'Başlangıç') {
            return true;
        }
        return itemDate.isAfter(dayjs('2023-08-31')) || itemDate.isSame(dayjs('2023-09-01'));
    });

    const trendChangeTableData = [...filteredTrendChanges.map((item, index) => ({ ...item, key: index }))];
    if (projection.length > 0) {
        const lastData = projection[projection.length - 1];
        const lastDataDate = dayjs(lastData.date);
        if (lastDataDate.isAfter(dayjs('2023-08-31')) || lastDataDate.isSame(dayjs('2023-09-01'))) {
            const lastTrendChangeDate = filteredTrendChanges.length > 0 
                ? dayjs(filteredTrendChanges[filteredTrendChanges.length - 1].date)
                : null;
            
            if (!lastTrendChangeDate || !lastDataDate.isSame(lastTrendChangeDate, 'month')) {
                trendChangeTableData.push({
                    date: lastData.date,
                    trend: 'end',
                    profit: lastData.profit,
                    cumulativeProfit: lastData.cumulativeProfit,
                    reason: 'Proje Sonu',
                    key: 'end'
                });
            }
        }
    }

    const bankDetailChartData = {
        labels: filteredProjection.map(item => formatDate(item.date)),
        datasets: [
            {
                label: 'Toplam Banka Geliri',
                data: filteredProjection.map(d => d.bankAmount),
                backgroundColor: '#4e73df',
                stack: 'Income'
            },
            {
                label: 'Toplam Kira Gideri',
                data: filteredProjection.map(d => -d.rentAmount),
                backgroundColor: '#e74a3b',
                stack: 'Expense'
            },
            {
                label: 'Net Kar/Zarar',
                data: filteredProjection.map(d => d.profit),
                type: 'line',
                borderColor: darkMode ? '#ffffff' : '#000000',
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: darkMode ? '#ffffff' : '#000000',
                pointBorderColor: darkMode ? '#ffffff' : '#000000',
                tension: 0.1,
                order: -1,
                fill: false
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: value => formatCurrency(value)
                }
            },
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatCurrency(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        animation: {
            duration: 1000
        }
    };

    const bankDetailChartOptions = {
        ...chartOptions,
        scales: {
            x: {
                stacked: true,
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    callback: value => formatCurrency(value)
                }
            }
        },
        plugins: {
            ...chartOptions.plugins,
            legend: {
                onClick: function (e, legendItem, legend) {
                    if (legendItem.text === 'Net Kar/Zarar') {
                        return;
                    }
                    const index = legendItem.datasetIndex;
                    const ci = legend.chart;
                    const meta = ci.getDatasetMeta(index);
                    meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;
                    ci.update();
                }
            }
        }
    };

    const trendChangeColumns = [
        {
            title: 'Tarih',
            dataIndex: 'date',
            key: 'date',
            width: 70,
            align: 'center',
            render: (date, record) => {
                if (!date) return '-';
                const formattedDate = formatDate(date);
                if (record.reason === 'Başlangıç' || record.reason === 'Proje Sonu') {
                    return `${formattedDate} - ${record.reason}`;
                }
                return formattedDate;
            }
        },
        {
            title: 'Yön Değişimi',
            dataIndex: 'trend',
            key: 'trend',
            width: 100,
            align: 'center',
            render: (trend, record) => {
                if (trend === 'end') {
                    return (
                        <span style={{ 
                            color: darkMode ? '#4da6ff' : '#1890ff',
                            fontSize: '10px',
                            fontWeight: 400,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                        }}>
                            <span>-</span>
                        </span>
                    );
                }
                return trend === 'up' 
                    ? (
                        <span style={{ 
                            color: darkMode ? '#73d13d' : '#52c41a', 
                            fontSize: '20px',
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                        }}>
                            ↑
                        </span>
                    )
                    : (
                        <span style={{ 
                            color: darkMode ? '#ff7875' : '#ff4d4f', 
                            fontSize: '20px',
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                        }}>
                            ↓
                        </span>
                    );
            }
        },
        {
            title: 'Kar/Zarar',
            dataIndex: 'profit',
            key: 'profit',
            width: 110,
            align: 'center',
            render: (profit) => {
                const isNegative = profit < 0;
                const color = isNegative
                    ? (darkMode ? '#ff7875' : '#ff4d4f')
                    : (darkMode ? '#73d13d' : '#52c41a');
                return (
                    <span style={{ 
                        color, 
                        fontWeight: 500,
                        fontSize: '13px',
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                    }}>
                        {formatCurrency(profit)}
                    </span>
                );
            }
        },
        {
            title: 'Birikimli Kar/Zarar',
            dataIndex: 'cumulativeProfit',
            key: 'cumulativeProfit',
            width: 120,
            align: 'center',
            render: (profit) => {
                const isNegative = profit < 0;
                const color = isNegative
                    ? (darkMode ? '#ff7875' : '#ff4d4f')
                    : (darkMode ? '#73d13d' : '#52c41a');
                return (
                    <span style={{ 
                        color, 
                        fontWeight: 400,
                        fontSize: '10px',
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}>
                        {formatCurrency(profit)}
                    </span>
                );
            }
        },
        {
            title: 'Değişim Nedeni',
            dataIndex: 'reason',
            key: 'reason',
            width: 260,
            align: 'left',
            render: (reason, record) => {
                if (record.reason === 'Başlangıç' || record.reason === 'Proje Sonu') {
                    return <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.45)' : '#999' }}>-</span>;
                }
                if (!reason || reason === 'null' || reason.includes('null')) {
                    return <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.45)' : '#999' }}>-</span>;
                }
                const cleanReason = reason.replace(/null/g, '').replace(/,,/g, ',').replace(/^,|,$/g, '').trim();
                return (
                    <span style={{ 
                        color: darkMode ? 'rgba(255, 255, 255, 0.65)' : '#595959',
                        fontSize: '12px',
                        lineHeight: '1.4'
                    }}>
                        {cleanReason || '-'}
                    </span>
                );
            }
        }
    ];

    const resultsTableColumns = [
        {
            title: 'Tarih',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (date) => {
                if (!date) return '-';
                return formatDate(date);
            }
        },
        {
            title: 'Toplam Banka Geliri',
            dataIndex: 'bankAmount',
            key: 'bankAmount',
            width: 180,
            render: (amount, record) => (
                <div>
                    {formatCurrency(amount)}
                    {record.bankDetails && record.bankDetails.length > 0 && (
                        <div style={{ fontSize: '0.85em', color: darkMode ? 'rgba(255, 255, 255, 0.45)' : '#666' }}>
                            ({record.bankDetails.length} aktif banka)
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Toplam Kira Gideri',
            dataIndex: 'rentAmount',
            key: 'rentAmount',
            width: 180,
            render: (amount, record) => {
                if (!record.rentDetails) {
                    return formatCurrency(amount || 0);
                }
                const hasDebt = record.rentDetails.some(detail => detail.debt && detail.debt > 0);
                const hasBankMultiplier = record.rentDetails.some(detail => detail.bankMultiplier && detail.bankMultiplier > 1);
                const totalDebt = record.rentDetails.reduce((sum, detail) => sum + (detail.debt || 0), 0);
                const multiplier = record.rentDetails.find(d => d.bankMultiplier > 1)?.bankMultiplier || 1;
                
                return (
                    <div>
                        {formatCurrency(amount || 0)}
                        {hasDebt && (
                            <div style={{ fontSize: '0.85em', color: darkMode ? '#ff7875' : '#ff4d4f' }}>
                                (Borç: {formatCurrency(totalDebt)})
                            </div>
                        )}
                        {hasBankMultiplier && (
                            <div style={{ fontSize: '0.85em', color: darkMode ? '#4da6ff' : '#1890ff' }}>
                                (Banka çarpanı: {multiplier}x)
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Net Kar',
            dataIndex: 'profit',
            key: 'profit',
            width: 130,
            render: (profit) => {
                const color = profit < 0 
                    ? (darkMode ? '#ff7875' : '#ff4d4f')
                    : (darkMode ? '#73d13d' : '#52c41a');
                return (
                    <span style={{ color }}>
                        {formatCurrency(profit || 0)}
                    </span>
                );
            }
        }
    ];

    return (
        <div style={{ padding: '8px' }}>
            <style>{`
                .ant-card-body { padding: 12px; }
                .ant-form-item { margin-bottom: 8px; }
                .ant-table-small .ant-table-tbody > tr > td { padding: 4px 8px; font-size: 0.85rem; }
                .ant-list-item { padding: 8px 12px; }
                .table-container { max-height: 500px; overflow-y: auto; }
                .chart-container { height: 400px; }
                .cumulative-chart-container { height: 450px; }
                .ant-list-item.inactive { background-color: #f8f9fa; color: #6c757d; }
                .table-secondary { background-color: #f8f9fa !important; font-weight: bold; }
            `}</style>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Finansal Projeksiyon Detaylı Görünüm</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                    >
                        <div className="chart-container">
                            {filteredProjection.length > 0 ? (
                                <Bar data={bankDetailChartData} options={bankDetailChartOptions} />
                            ) : (
                                <div style={{ 
                                    height: '100%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: darkMode ? '#e0e0e0' : '#999'
                                }}>
                                    Grafik verisi bulunamadı
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Birikimli Kar/Zarar Grafiği</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                    >
                        <div className="cumulative-chart-container">
                            <CumulativeProfitChart 
                                maptriksId={maptriksId}
                                projection={projection}
                                autoSave={true}
                            />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12} xl={6}>
                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Banka Sözleşmesi Ekle</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                        style={{ marginBottom: 8 }}
                    >
                        <Form form={bankForm} onFinish={handleBankSubmit} layout="vertical" size="small">
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item name="name" label={<span style={{ fontSize: '0.85rem' }}>Sözleşme Adı</span>} rules={[{ required: true }]}>
                                        <Input size="small" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="amount" label={<span style={{ fontSize: '0.85rem' }}>Yıllık Tutar</span>} rules={[{ required: true }]}>
                                        <InputNumber {...inputNumberMoneyProps} size="small" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item name="startDate" label={<span style={{ fontSize: '0.85rem' }}>Başlangıç Tarihi</span>} rules={[{ required: true }]}>
                                        <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="endDate" label={<span style={{ fontSize: '0.85rem' }}>Bitiş Tarihi</span>}>
                                        <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="rate" label={<span style={{ fontSize: '0.85rem' }}>Yıllık Artış Oranı</span>} initialValue="0.25">
                                <Select size="small">
                                    <Option value="0">Artış Yok</Option>
                                    <Option value="0.25">%25</Option>
                                    <Option value="0.30">%30</Option>
                                    <Option value="0.35">%35</Option>
                                    <Option value="ufe">ÜFE</Option>
                                    <Option value="tufe">TÜFE</Option>
                                    <Option value="ufetufe">ÜFE+TÜFE/2</Option>
                                </Select>
                            </Form.Item>
                            <Button type="primary" htmlType="submit" size="small">
                                Banka Sözleşmesi Ekle
                            </Button>
                        </Form>
                    </Card>

                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Kira Sözleşmesi Ekle</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                        style={{ marginBottom: 8 }}
                    >
                        <Form form={rentForm} onFinish={handleRentSubmit} layout="vertical" size="small">
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item name="name" label={<span style={{ fontSize: '0.85rem' }}>Sözleşme Adı</span>} rules={[{ required: true }]}>
                                        <Input size="small" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="amount" label={<span style={{ fontSize: '0.85rem' }}>Yıllık Tutar</span>} rules={[{ required: true }]}>
                                        <InputNumber {...inputNumberMoneyProps} size="small" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item name="startDate" label={<span style={{ fontSize: '0.85rem' }}>Başlangıç Tarihi</span>} rules={[{ required: true }]}>
                                        <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="endDate" label={<span style={{ fontSize: '0.85rem' }}>Bitiş Tarihi</span>}>
                                        <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item name="paymentType" label={<span style={{ fontSize: '0.85rem' }}>Ödeme Tipi</span>} initialValue="fixed">
                                        <Select size="small">
                                            <Option value="fixed">Sabit (Her Ay)</Option>
                                            <Option value="bankBased">Banka Girdikçe</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="rate" label={<span style={{ fontSize: '0.85rem' }}>Yıllık Artış Oranı</span>} initialValue="0.25">
                                        <Select size="small">
                                            <Option value="0">Artış Yok</Option>
                                            <Option value="0.25">%25</Option>
                                            <Option value="0.30">%30</Option>
                                            <Option value="0.35">%35</Option>
                                            <Option value="ufe">ÜFE</Option>
                                            <Option value="tufe">TÜFE</Option>
                                            <Option value="ufetufe">ÜFE+TÜFE/2</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Button type="primary" htmlType="submit" size="small" block>
                                Kira Sözleşmesi Ekle
                            </Button>
                        </Form>
                    </Card>

                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Enflasyon Oranları (2025-2036)</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                    >
                        <Form form={inflationForm} onFinish={handleInflationSubmit} layout="vertical" size="small">
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item name="year" label={<span style={{ fontSize: '0.85rem' }}>Yıl</span>} initialValue="2025">
                                        <Select size="small">
                                            <Option value="2025">2025</Option>
                                            <Option value="2026">2026</Option>
                                            <Option value="2027">2027</Option>
                                            <Option value="2028">2028</Option>
                                            <Option value="2029">2029</Option>
                                            <Option value="2030">2030</Option>
                                            <Option value="2031">2031</Option>
                                            <Option value="2032">2032</Option>
                                            <Option value="2033">2033</Option>
                                            <Option value="2034">2034</Option>
                                            <Option value="2035">2035</Option>
                                            <Option value="2036">2036</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="ufe" label={<span style={{ fontSize: '0.85rem' }}>ÜFE (%)</span>} rules={[{ required: true }]}>
                                        <InputNumber size="small" style={{ width: '100%' }} step={0.01} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="tufe" label={<span style={{ fontSize: '0.85rem' }}>TÜFE (%)</span>} rules={[{ required: true }]}>
                                        <InputNumber size="small" style={{ width: '100%' }} step={0.01} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Button type="default" htmlType="submit" size="small" block>
                                Enflasyon Oranı Ekle/Güncelle
                            </Button>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} md={12} xl={6}>
                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Aktif Banka Sözleşmeleri</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                        style={{ marginBottom: 8 }}
                    >
                        <List
                            size="small"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                            dataSource={bankContracts}
                            renderItem={(item, index) => {
                                const isActive = item.active !== false;
                                return (
                                    <List.Item
                                        className={!isActive ? 'inactive' : ''}
                                        actions={[
                                            <Button
                                                key="toggle"
                                                type={isActive ? 'primary' : 'default'}
                                                size="small"
                                                icon={isActive ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                onClick={() => toggleBankContractActive(index)}
                                            />,
                                            <Button
                                                key="edit"
                                                type="link"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => editBankContract(index)}
                                            />,
                                            <Popconfirm
                                                key="delete"
                                                title="Silmek istediğinize emin misiniz?"
                                                onConfirm={() => deleteBankContract(index)}
                                            >
                                                <Button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                />
                                            </Popconfirm>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={<strong>{item.name}</strong>}
                                            description={
                                                <div>
                                                    <div><small>Yıllık: {formatCurrency(item.amount)}</small></div>
                                                    <div><small>Başlangıç: {formatDate(item.startDate)}</small></div>
                                                    <div><small>Bitiş: {item.endDate ? formatDate(item.endDate) : 'Süresiz'}</small></div>
                                                    <div><small>Artış: {getRateDisplay(item.rate)}</small></div>
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                    </Card>

                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Aktif Kira Sözleşmeleri</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                        style={{ marginBottom: 8}}
                    >
                        <List
                            size="small"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                            dataSource={rentContracts}
                            renderItem={(item, index) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="edit"
                                            type="link"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => editRentContract(index)}
                                        />,
                                        <Popconfirm
                                            key="delete"
                                            title="Silmek istediğinize emin misiniz?"
                                            onConfirm={() => deleteRentContract(index)}
                                        >
                                            <Button
                                                type="link"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={<strong>{item.name}</strong>}
                                        description={
                                            <div>
                                                <div><small>Yıllık: {formatCurrency(item.amount)}</small></div>
                                                <div><small>Başlangıç: {formatDate(item.startDate)}</small></div>
                                                <div><small>Bitiş: {item.endDate ? formatDate(item.endDate) : 'Süresiz'}</small></div>
                                                <div><small>Ödeme: {item.paymentType === 'bankBased' ? 'Banka Girdikçe' : 'Sabit (Her Ay)'}</small></div>
                                                <div><small>Artış: {getRateDisplay(item.rate)}</small></div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>

                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem' }}>Tanımlı Enflasyon Oranları</span>}
                        headStyle={{ backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                    >
                        <div style={{ marginBottom: 8 }}>
                            <Alert
                                message={<small><strong>2022:</strong> ÜFE: %97.72, TÜFE: %64.27</small>}
                                type="info"
                                size="small"
                                style={{ marginBottom: 4, padding: '4px 8px' }}
                            />
                            <Alert
                                message={<small><strong>2023:</strong> ÜFE: %44.22, TÜFE: %64.77</small>}
                                type="info"
                                size="small"
                                style={{ marginBottom: 4, padding: '4px 8px' }}
                            />
                            <Alert
                                message={<small><strong>2024:</strong> ÜFE: %28.52, TÜFE: %44.38</small>}
                                type="info"
                                size="small"
                                style={{ marginBottom: 4, padding: '4px 8px' }}
                            />
                        </div>
                        <List
                            size="small"
                            dataSource={Object.entries(inflationRates)}
                            renderItem={([year, rates]) => (
                                <List.Item
                                    actions={[
                                        <Popconfirm
                                            key="delete"
                                            title="Silmek istediğinize emin misiniz?"
                                            onConfirm={() => deleteInflationRate(year)}
                                        >
                                            <Button
                                                type="link"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={<strong>{year}</strong>}
                                        description={
                                            <div>
                                                <div><small>ÜFE: %{rates.ufe}</small></div>
                                                <div><small>TÜFE: %{rates.tufe}</small></div>
                                                <div><small>ÜFE+TÜFE/2: %{((rates.ufe + rates.tufe) / 2).toFixed(2)}</small></div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        size="small"
                        title={<span style={{ fontSize: '0.9rem', color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Kar-Zarar Yön Değişimleri</span>}
                        headStyle={{ 
                            backgroundColor: darkMode ? '#262626' : '#f8f9fa', 
                            padding: '8px 12px',
                            borderBottom: darkMode ? '1px solid #303030' : undefined
                        }}
                        style={{ 
                            marginBottom: 16,
                            backgroundColor: darkMode ? '#1f1f1f' : undefined,
                            borderColor: darkMode ? '#303030' : undefined
                        }}
                    >
                        <div className="table-container">
                            <Table
                                size="small"
                                columns={trendChangeColumns}
                                dataSource={trendChangeTableData}
                                pagination={false}
                                scroll={{ x: 'max-content', y: 400 }}
                                rowClassName={(record) => record.key === 'end' ? 'table-secondary' : ''}
                                className={darkMode ? 'dark-table' : ''}
                                style={{
                                    backgroundColor: darkMode ? '#1f1f1f' : undefined,
                                    color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined
                                }}
                            />
                        </div>
                    </Card>

                    <Card 
                        size="small" 
                        title={<span style={{ fontSize: '0.9rem', color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Aylık Tahminler</span>}
                        headStyle={{ 
                            backgroundColor: darkMode ? '#262626' : '#f8f9fa', 
                            padding: '8px 12px',
                            borderBottom: darkMode ? '1px solid #303030' : undefined
                        }}
                        style={{
                            backgroundColor: darkMode ? '#1f1f1f' : undefined,
                            borderColor: darkMode ? '#303030' : undefined
                        }}
                    >
                        <div className="table-container">
                            <Table
                                size="small"
                                columns={resultsTableColumns}
                                dataSource={filteredProjection.map((item, index) => ({ ...item, key: index }))}
                                pagination={false}
                                scroll={{ x: 'max-content', y: 400 }}
                                className={darkMode ? 'dark-table' : ''}
                                style={{
                                    backgroundColor: darkMode ? '#1f1f1f' : undefined,
                                    color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined
                                }}
                            />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Banka Sözleşmesi Düzenle"
                open={editBankModalVisible}
                onCancel={() => {
                    setEditBankModalVisible(false);
                    setEditingBankIndex(null);
                }}
                footer={null}
            >
                <Form form={editBankForm} onFinish={handleBankEditSubmit} layout="vertical" size="small">
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="name" label="Sözleşme Adı" rules={[{ required: true }]}>
                                <Input size="small" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="amount" label="Yıllık Tutar" rules={[{ required: true }]}>
                                <InputNumber {...inputNumberMoneyProps} size="small" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="startDate" label="Başlangıç Tarihi" rules={[{ required: true }]}>
                                <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="endDate" label="Bitiş Tarihi">
                                <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="rate" label="Yıllık Artış Oranı">
                        <Select size="small">
                            <Option value="0">Artış Yok</Option>
                            <Option value="0.25">%25</Option>
                            <Option value="0.30">%30</Option>
                            <Option value="0.35">%35</Option>
                            <Option value="ufe">ÜFE</Option>
                            <Option value="tufe">TÜFE</Option>
                            <Option value="ufetufe">ÜFE+TÜFE/2</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={() => {
                                setEditBankModalVisible(false);
                                setEditingBankIndex(null);
                            }}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Kaydet
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Kira Sözleşmesi Düzenle"
                open={editRentModalVisible}
                onCancel={() => {
                    setEditRentModalVisible(false);
                    setEditingRentIndex(null);
                }}
                footer={null}
            >
                <Form form={editRentForm} onFinish={handleRentEditSubmit} layout="vertical" size="small">
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="name" label="Sözleşme Adı" rules={[{ required: true }]}>
                                <Input size="small" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="amount" label="Yıllık Tutar" rules={[{ required: true }]}>
                                <InputNumber {...inputNumberMoneyProps} size="small" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="startDate" label="Başlangıç Tarihi" rules={[{ required: true }]}>
                                <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="endDate" label="Bitiş Tarihi">
                                <DatePicker size="small" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="paymentType" label="Ödeme Tipi">
                                <Select size="small">
                                    <Option value="fixed">Sabit (Her Ay)</Option>
                                    <Option value="bankBased">Banka Girdikçe</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="rate" label="Yıllık Artış Oranı">
                                <Select size="small">
                                    <Option value="0">Artış Yok</Option>
                                    <Option value="0.25">%25</Option>
                                    <Option value="0.30">%30</Option>
                                    <Option value="0.35">%35</Option>
                                    <Option value="ufe">ÜFE</Option>
                                    <Option value="tufe">TÜFE</Option>
                                    <Option value="ufetufe">ÜFE+TÜFE/2</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Space>
                            <Button onClick={() => {
                                setEditRentModalVisible(false);
                                setEditingRentIndex(null);
                            }}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Kaydet
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default OngoruPage;